import { useMemo, type ReactElement } from 'react'
import { Vector3 } from 'three'
import { common } from '@mui/material/colors'
import { useAppStore } from '@stores'
import Text3DThreeDoubleSided from 'src/components/scene/Text3DThreeDoubleSided'
import {
  ENVELOPE_LABEL_BORDER_WIDTH,
  ENVELOPE_TITLE_SCALE_FACTOR,
  getEnvelopeTitlePosition,
} from './utils'

interface Props {
  title: string
  axis: [Vector3, Vector3]
  ordinateDirection: Vector3
  maxOrdinate: number
  textXDirection: Vector3
}

/**
 * Diagram title (My / Vz) centered above the plot frame spanned by the max ordinate: black text on
 * a white box with a black border, twice the size of the extreme value labels, drawn on top of the
 * scene like them.
 */
const EnvelopeTitleLabel = ({
  title,
  axis,
  ordinateDirection,
  maxOrdinate,
  textXDirection,
}: Props): ReactElement => {
  const textScale = useAppStore(state => state.loadRenderConfig.lineLoads.textScale)

  const position = useMemo(
    () => getEnvelopeTitlePosition(axis, ordinateDirection, maxOrdinate, textScale),
    [axis, ordinateDirection, maxOrdinate, textScale],
  )
  const textLines = useMemo(() => [title], [title])

  return (
    <Text3DThreeDoubleSided
      textLines={textLines}
      color={common.black}
      backgroundColor={common.white}
      borderWidth={ENVELOPE_LABEL_BORDER_WIDTH}
      borderColor={common.black}
      position={position}
      anchorLocation="bottom"
      scale={textScale * ENVELOPE_TITLE_SCALE_FACTOR}
      textXOrientation={textXDirection}
      textYOrientation={ordinateDirection}
      disableDepthTest
    />
  )
}

export default EnvelopeTitleLabel
